import { useState, useCallback } from 'react';
import { adminApi } from '../api/client';
import { useApi } from '../hooks/useApi';
import { Loading, ApiError, EmptyState } from '../components/ui/Loading';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { Avatar } from '../components/ui/Avatar';
import { Pagination } from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { formatDate, formatDateTime, timeAgo, paginate } from '../utils/helpers';

// ─── Onglet 1 : Comptes en attente (ancien workflow auth/register cat A) ───────
const PendingAccountsTab = () => {
  const toast = useToast();
  const { data: raw, loading, error, refetch, setData } = useApi(() => adminApi.getPendingValidations());
  const [selected,    setSelected]    = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [reason,      setReason]      = useState('');
  const [actioning,   setActioning]   = useState(false);
  const items = raw?.data || [];

  const removeItem = useCallback((userId) => {
    setData(prev => ({ ...prev, data: prev.data.filter(i => i.user_id !== userId) }));
  }, [setData]);

  const handleAction = async (userId, action, rejection_reason = null) => {
    setActioning(true);
    try {
      await adminApi.validateUser(userId, { action, ...(rejection_reason ? { rejection_reason } : {}) });
      removeItem(userId);
      toast.success(action === 'approve'
        ? '✓ Compte approuvé — Email avec accès envoyé automatiquement'
        : 'Compte rejeté — Email de notification envoyé');
      setSelected(null); setRejectModal(null); setReason('');
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setActioning(false); }
  };

  if (loading) return <Loading text="Chargement des validations..." />;
  if (error)   return <ApiError message={error} onRetry={refetch} />;

  return (
    <div>
      {items.length > 0 && (
        <div className="inline-alert inline-alert-yellow" style={{ marginBottom: 20 }}>
          <span>📋</span>
          <div>
            <strong>{items.length} compte(s) entreprise</strong> en attente d'activation —
            Ces entreprises ont créé un compte via l'application mobile.
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <div className="card"><EmptyState icon="✅" title="Tout est traité" text="Aucun compte entreprise en attente d'activation." /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {items.map(item => (
            <div key={item.user_id} className="card" style={{ borderLeft: '3px solid var(--orange)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <Avatar name={item.name} size="lg" />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16, fontFamily: 'Syne', marginBottom: 3 }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{item.email}</div>
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>{item.phone} · {formatDate(item.created_at)} ({timeAgo(item.created_at)})</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSelected(item)}>Détails</button>
                  <button className="btn btn-danger btn-sm" onClick={() => { setRejectModal(item); setReason(''); }} disabled={actioning}>✕ Rejeter</button>
                  <button className="btn btn-success btn-sm" onClick={() => handleAction(item.user_id, 'approve')} disabled={actioning}>
                    {actioning ? <span className="spinner" style={{ width: 12, height: 12 }} /> : '✓ Approuver'}
                  </button>
                </div>
              </div>
              <div className="divider" />
              <div className="grid-3" style={{ gap: 10 }}>
                {[
                  { label: '🏢 Entreprise',  val: item.company_name },
                  { label: '📄 Registre',    val: item.company_registration },
                  { label: '🆔 ICE',         val: item.company_tax_id || <span style={{ color: 'var(--text3)' }}>Non fourni</span> },
                ].map((f, i) => (
                  <div key={i} style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{f.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{f.val}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Compte en attente" size="md"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setSelected(null)}>Fermer</button>
          <button className="btn btn-danger" onClick={() => { setRejectModal(selected); setSelected(null); }}>✕ Rejeter</button>
          <button className="btn btn-success" onClick={() => handleAction(selected?.user_id, 'approve')} disabled={actioning}>✓ Approuver</button>
        </>}>
        {selected && (
          <div>
            <div className="detail-header"><Avatar name={selected.name} size="lg" />
              <div><div className="detail-name">{selected.name}</div><div className="detail-meta">{selected.email} · {selected.phone}</div></div>
            </div>
            {[
              { l: 'Entreprise',  v: selected.company_name },
              { l: 'Registre',    v: selected.company_registration },
              { l: 'ICE',         v: selected.company_tax_id || '—' },
              { l: 'Catégorie',   v: 'A — Entreprise' },
              { l: 'Demande le',  v: formatDateTime(selected.created_at) },
            ].map((r, i) => (
              <div key={i} className="info-row"><span className="info-label">{r.l}</span><span className="info-value">{r.v}</span></div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Rejeter le compte" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Annuler</button>
          <button className="btn btn-danger" onClick={() => handleAction(rejectModal?.user_id, 'reject', reason)} disabled={actioning || !reason.trim()}>
            {actioning ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Confirmer le rejet'}
          </button>
        </>}>
        {rejectModal && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Rejeter <strong>{rejectModal.name}</strong> ({rejectModal.company_name}).
              Un email de notification sera envoyé.
            </p>
            <div className="form-group">
              <label className="form-label">Raison du rejet *</label>
              <textarea className="form-textarea" placeholder="Ex: Documents incomplets..." value={reason} onChange={e => setReason(e.target.value)} />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

// ─── Onglet 2 : Demandes entreprise v2.2 (formulaire public web) ──────────────
const EnterpriseRequestsTab = () => {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search,       setSearch]       = useState('');
  const [page,         setPage]         = useState(1);
  const [selected,     setSelected]     = useState(null);
  const [rejectModal,  setRejectModal]  = useState(null);
  const [reason,       setReason]       = useState('');
  const [language,     setLanguage]     = useState('fr');
  const [actioning,    setActioning]    = useState(false);

  const { data: raw, loading, error, refetch } = useApi(
    () => adminApi.getEnterpriseRequests({ status: statusFilter }),
    [statusFilter]
  );

  const requests = raw?.data || [];
  const filtered = requests.filter(r => {
    const q = search.toLowerCase();
    return !q || r.company_name?.toLowerCase().includes(q) || r.contact_name?.toLowerCase().includes(q) || r.contact_email?.toLowerCase().includes(q);
  });
  const paged = paginate(filtered, page, 10);

  const STATUS_CFG = {
    pending:  { label: 'En attente', cls: 'badge-yellow' },
    approved: { label: 'Approuvée',  cls: 'badge-green' },
    rejected: { label: 'Rejetée',    cls: 'badge-red' },
  };

  const handleApprove = async () => {
    setActioning(true);
    try {
      await adminApi.approveEnterpriseRequest(selected.request_id, { language });
      toast.success(`✓ Compte créé — Email + mot de passe temporaire envoyé à ${selected.contact_email}`);
      setSelected(null); refetch();
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setActioning(false); }
  };

  const handleReject = async () => {
    setActioning(true);
    try {
      await adminApi.rejectEnterpriseRequest(rejectModal.request_id, { rejection_reason: reason });
      toast.success('Demande rejetée — Email de notification envoyé');
      setRejectModal(null); setReason(''); refetch();
    } catch (err) { toast.error(err.message || 'Erreur'); }
    finally { setActioning(false); }
  };

  return (
    <div>
      <div className="inline-alert inline-alert-blue" style={{ marginBottom: 20 }}>
        <span>🆕</span>
        <div>
          <strong>Workflow v2.2 :</strong> Les entreprises remplissent un formulaire public (sans créer de compte).
          L'admin crée le compte, le système génère un <strong>mot de passe temporaire</strong> et envoie un email automatiquement.
          Le client devra le changer à sa première connexion.
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input className="search-input" placeholder="Entreprise, contact, email..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="pending">En attente</option>
          <option value="approved">Approuvées</option>
          <option value="rejected">Rejetées</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>{filtered.length} demandes</span>
        <button className="btn btn-ghost btn-sm" onClick={refetch}>↺</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <Loading text="Chargement..." />
          : error ? <ApiError message={error} onRetry={refetch} />
          : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Entreprise</th><th>Contact</th><th>Secteur</th><th>Statut</th><th>Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {paged.items.map(r => {
                    const sc = STATUS_CFG[r.status] || { label: r.status, cls: 'badge-gray' };
                    return (
                      <tr key={r.request_id}>
                        <td>
                          <div className="td-main">{r.company_name}</div>
                          <div className="td-sub">{r.company_registration || '—'}</div>
                        </td>
                        <td>
                          <div className="td-main">{r.contact_name}</div>
                          <div className="td-sub">{r.contact_email}</div>
                          <div className="td-sub">{r.contact_phone}</div>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--text2)' }}>{r.business_type || '—'}</td>
                        <td><Badge className={sc.cls}>{sc.label}</Badge></td>
                        <td style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                          {formatDate(r.created_at)}
                          <div style={{ color: 'var(--text3)' }}>{timeAgo(r.created_at)}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setSelected(r)}>Voir</button>
                            {r.status === 'pending' && <>
                              <button className="btn btn-danger btn-sm" onClick={() => { setRejectModal(r); setReason(''); }}>✕</button>
                              <button className="btn btn-success btn-sm" onClick={() => { setSelected(r); }}>✓ Créer</button>
                            </>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {paged.items.length === 0 && !loading && (
                <EmptyState icon="🏢" title="Aucune demande" text="Aucune demande entreprise dans cette catégorie." />
              )}
            </div>
            {paged.total > 0 && <div style={{ padding: '0 16px 16px' }}><Pagination {...paged} onPage={setPage} /></div>}
          </>
        )}
      </div>

      {/* DETAIL + APPROBATION */}
      <Modal open={!!selected} onClose={() => setSelected(null)}
        title={selected?.status === 'pending' ? `Créer le compte — ${selected?.company_name}` : `Demande — ${selected?.company_name}`}
        size="md"
        footer={selected?.status === 'pending' ? <>
          <button className="btn btn-secondary" onClick={() => setSelected(null)}>Annuler</button>
          <button className="btn btn-danger" onClick={() => { setRejectModal(selected); setSelected(null); }}>✕ Rejeter</button>
          <button className="btn btn-success" onClick={handleApprove} disabled={actioning}>
            {actioning ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '✓ Créer le compte & Envoyer email'}
          </button>
        </> : <button className="btn btn-secondary" onClick={() => setSelected(null)}>Fermer</button>}
      >
        {selected && (
          <div>
            <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'Syne', marginBottom: 4 }}>🏢 {selected.company_name}</div>
              {selected.description && <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>{selected.description}</div>}
            </div>
            {[
              { l: 'Contact',    v: selected.contact_name },
              { l: 'Email',      v: selected.contact_email },
              { l: 'Téléphone',  v: selected.contact_phone },
              { l: 'Registre',   v: selected.company_registration || '—' },
              { l: 'ICE',        v: selected.company_tax_id || '—' },
              { l: 'Secteur',    v: selected.business_type || '—' },
              { l: 'Adresse',    v: selected.address || '—' },
              { l: 'Demande le', v: formatDateTime(selected.created_at) },
            ].map((r, i) => (
              <div key={i} className="info-row"><span className="info-label">{r.l}</span><span className="info-value">{r.v}</span></div>
            ))}

            {selected.status === 'pending' && (
              <>
                <div className="divider" />
                <div className="inline-alert inline-alert-green" style={{ marginBottom: 16 }}>
                  <span>📧</span>
                  <div style={{ fontSize: 12 }}>
                    Un compte <strong>Catégorie A</strong> sera créé. Un email avec <strong>mot de passe temporaire</strong> sera envoyé à <strong>{selected.contact_email}</strong>.
                    Le client devra le changer à la première connexion (<code>password_reset_required = true</code>).
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Langue de l'email de bienvenue</label>
                  <select className="form-select" value={language} onChange={e => setLanguage(e.target.value)}>
                    <option value="fr">Français</option>
                    <option value="ar">Arabe (Darija)</option>
                    <option value="en">Anglais</option>
                  </select>
                </div>
              </>
            )}
            {selected.status === 'rejected' && selected.rejection_reason && (
              <div className="inline-alert inline-alert-red" style={{ marginTop: 12 }}>
                <span>❌</span>
                <div><strong>Raison du rejet :</strong> {selected.rejection_reason}</div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* REJECT */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Rejeter la demande" size="sm"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Annuler</button>
          <button className="btn btn-danger" onClick={handleReject} disabled={actioning || !reason.trim()}>
            {actioning ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Rejeter'}
          </button>
        </>}>
        {rejectModal && (
          <>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              Rejeter la demande de <strong>{rejectModal.company_name}</strong>. Le demandeur sera notifié par email.
            </p>
            <div className="form-group">
              <label className="form-label">Raison du rejet *</label>
              <textarea className="form-textarea" placeholder="Ex: Secteur non éligible, informations insuffisantes..." value={reason} onChange={e => setReason(e.target.value)} />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

// ─── Page principale ──────────────────────────────────────────────────────────
export const Validations = () => {
  const [tab, setTab] = useState('enterprise_requests');
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 4, width: 'fit-content' }}>
        {[
          { key: 'enterprise_requests', label: '🏢 Demandes entreprises', sub: 'Formulaire web public (v2.2)' },
          { key: 'pending_accounts',    label: '✅ Comptes en attente',    sub: 'Inscription app mobile' },
        ].map(t => (
          <button key={t.key}
            className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.key)}
            style={{ borderRadius: 9, flexDirection: 'column', height: 'auto', padding: '8px 16px', gap: 2 }}>
            <span>{t.label}</span>
            <span style={{ fontSize: 10, opacity: 0.65, fontWeight: 400 }}>{t.sub}</span>
          </button>
        ))}
      </div>
      {tab === 'enterprise_requests' && <EnterpriseRequestsTab />}
      {tab === 'pending_accounts'    && <PendingAccountsTab />}
    </div>
  );
};
